/**
 * La voz de Donna: cómo suena cuando habla. Cálida y cercana, pero breve y segura: dice lo que hizo, propone el siguiente paso y no se enreda.
 * Las frases tienen varias versiones para que no suene a máquina (se elige con `azar`, que en las pruebas es fijo).
 * Privacidad: en «modo discreto» (el de siempre) nunca dice nombres de pacientes en voz alta; la sala de espera puede estar cerca.
 * Seguridad: por voz Donna no da diagnósticos ni dosis y nunca firma, guarda, cobra ni envía; solo gestiona y recuerda.
 */
import type { Orden, Pestana } from "./ordenes.ts";

export type Azar = () => number;
const elegir = <T>(xs: readonly T[], azar: Azar): T => xs[Math.min(xs.length - 1, Math.floor(azar() * xs.length))];

const TITULOS = /^(dr|dra|doctor|doctora|odont[oó]log[oa]|lic|licenciad[oa]|ing|sr|sra|se[ñn]or|se[ñn]ora|srta|prof)\.?\s+/i;
/** «DRA. LUCÍA PAREDES» → «Lucía». Solo el primer nombre, sin títulos. */
export function primerNombre(nombre: string): string {
  let n = nombre.trim();
  while (TITULOS.test(n)) n = n.replace(TITULOS, "");
  const p = n.split(/\s+/)[0] ?? "";
  return p ? p.charAt(0).toLocaleUpperCase("es") + p.slice(1).toLocaleLowerCase("es") : "";
}

export const saludoSegunHora = (hora: number) => (hora < 12 ? "Buenos días" : hora < 19 ? "Buenas tardes" : "Buenas noches");

/** «14:30» → «las 2 y media de la tarde»; «09:00» → «las 9 de la mañana»; «13:00» → «la una de la tarde». */
export function horaHablada(hhmm: string): string {
  const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return hhmm;
  const h = Number(m[1]), min = Number(m[2]);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const momento = h === 12 ? "del mediodía" : h < 12 ? "de la mañana" : h < 19 ? "de la tarde" : "de la noche";
  const minutos = min === 0 ? "" : min === 30 ? " y media" : ` y ${min}`;
  return `${h12 === 1 ? "la una" : `las ${h12}`}${minutos} ${momento}`;
}

/* ---------- respuestas a las órdenes ---------- */
// Frases cortas y naturales, como diría una asistente de confianza: sin exclamaciones, sin repetir el nombre a cada rato y sin «con muchísimo gusto».

const PESTANA_HABLADA: Record<Pestana, string> = {
  consulta: "la consulta", ficha: "la ficha", odontograma: "el odontograma", evolucion: "la evolución", especialidades: "las especialidades", plan: "el plan y los pagos", fotos: "las fotos", documentos: "los documentos",
};
const DESTINO: Record<string, readonly string[]> = {
  inicio: ["Listo, el inicio.", "Aquí está el inicio."],
  agenda: ["Listo, la agenda.", "Aquí tienes la agenda."],
  mensajes: ["Aquí están los mensajes.", "Listo, los mensajes."],
  cumpleanos: ["Estos son los cumpleaños.", "Listo, los cumpleaños."],
  configuracion: ["Listo, la configuración.", "Aquí está la configuración."],
  respaldos: ["Aquí están los respaldos.", "Listo, los respaldos."],
  reportes: ["Aquí están los reportes.", "Listo, los reportes."],
  facturas: ["Listo, las facturas.", "Aquí está la facturación."],
  nuevo: ["Listo, paciente nuevo.", "Vamos con el paciente nuevo."],
};

export interface ContextoVoz { azar: Azar; nombre?: string; discreto?: boolean }

/** Lo que Donna dice en voz alta ante una orden (la respuesta escrita en pantalla es otra, más técnica). */
export function habladoOrden(o: Orden, c: ContextoVoz): string {
  const a = c.azar;
  switch (o.tipo) {
    case "ir": return elegir(DESTINO[o.destino] ?? ["Listo."], a);
    case "pestana": return elegir([`Listo, ${PESTANA_HABLADA[o.pestana]}.`, `Abro ${PESTANA_HABLADA[o.pestana]}.`], a);
    case "buscar": return c.discreto ? elegir(["Un momento, lo busco.", "Lo busco."], a) : `Busco a ${o.texto}.`;
    case "elegir": return `Abro el número ${o.n}.`;
    case "dictar": return elegir(["Te escucho.", "Adelante, dicta."], a);
    case "detener_dictado": return elegir(["Listo, dejé de dictar.", "Dictado detenido."], a);
    case "historia_pdf": return "Preparo la historia clínica para imprimir o guardar en PDF.";
    case "ayuda": return "Puedo abrir pacientes, la agenda y los mensajes, y escuchar tus dictados. La lista completa quedó en pantalla.";
    case "no_permitida": return "Eso lo haces tú en la pantalla, por seguridad.";
    case "resumen_dia": return elegir(["Un momento, reviso tu agenda.", "Claro, te cuento."], a);
    case "charla": return charla(o.clase, c);
    case "desconocida": return o.texto ? elegir(["No te entendí, ¿puedes repetirlo?", "Perdona, ¿me lo repites?"], a) : elegir(["Dime.", "Te escucho."], a);
  }
}

function charla(clase: "gracias" | "estado" | "despedida" | "saludo", c: ContextoVoz): string {
  switch (clase) {
    case "gracias": return elegir(["De nada.", "Con gusto.", "Para eso estoy."], c.azar);
    case "estado": return elegir(["Muy bien. ¿En qué te ayudo?", "Bien, gracias. Dime."], c.azar);
    case "despedida": return elegir(["Hasta luego.", "Cuídate."], c.azar);
    case "saludo": return elegir(["Hola, ¿en qué te ayudo?", "Hola, dime."], c.azar);
  }
}

/** Abrir a un paciente: en modo discreto no se dice su nombre. */
export function habladoApertura(c: ContextoVoz & { pestana?: Pestana; nombrePaciente: string }): string {
  const donde = c.pestana ? PESTANA_HABLADA[c.pestana] : "la ficha";
  if (c.discreto) return elegir([`Listo, ${donde}.`, `Abro ${donde}.`], c.azar);
  return `Abro ${donde} de ${c.nombrePaciente}.`;
}

/** Varios pacientes con el mismo nombre. */
export function habladoVarios(c: ContextoVoz & { total: number; nombres: string[] }): string {
  if (c.discreto) return `Encontré ${c.total} pacientes. Están en pantalla: dime cuál, por ejemplo «el primero».`;
  return `Encontré ${c.total} pacientes: ${c.nombres.slice(0, 3).join(", ")}${c.total > 3 ? " y otros" : ""}. Dime cuál, por ejemplo «el primero».`;
}

export function habladoNoEncontrado(c: ContextoVoz & { texto: string }): string {
  return c.discreto ? "No encontré ese nombre. Revisa cómo se escribe o dilo con apellido." : `No encontré a ${c.texto}. Revisa el nombre o dilo con apellido.`;
}

/* ---------- el resumen del día ---------- */

export interface CitaDelDia { estado: string; hora: string }
export interface DatosDia { citas: number; atendidas: number; pendientes: number; proximaHora: string | null; sinConfirmar: number }

/** De las citas de hoy: cuántas hay, cuántas ya se atendieron, cuántas faltan (de esta hora en adelante), la próxima y cuántas sin confirmar. Las canceladas no cuentan. */
export function datosDelDia(citas: readonly CitaDelDia[], ahoraHHMM: string): DatosDia {
  const vigentes = citas.filter((c) => c.estado !== "cancelada");
  const atendidas = vigentes.filter((c) => c.estado === "atendida").length;
  const porVenir = vigentes.filter((c) => (c.estado === "agendada" || c.estado === "confirmada") && c.hora >= ahoraHHMM).sort((a, b) => a.hora.localeCompare(b.hora));
  return { citas: vigentes.length, atendidas, pendientes: porVenir.length, proximaHora: porVenir[0]?.hora ?? null, sinConfirmar: porVenir.filter((c) => c.estado === "agendada").length };
}

const plural = (n: number, uno: string, varios: string) => (n === 1 ? uno : varios);

/** El saludo y el resumen de la jornada, sin nombres de pacientes. Breve, y termina ofreciendo el siguiente paso cuando hay uno. */
export function resumenDelDia(d: DatosDia, c: { nombre?: string; hora: number; azar: Azar }): string {
  const n = c.nombre ? primerNombre(c.nombre) : "";
  const saludo = `${saludoSegunHora(c.hora)}${n ? `, ${n}` : ""}.`;
  if (d.citas === 0) return `${saludo} Hoy no tienes citas. ¿Quieres que te ayude a llenar la agenda?`;
  let t = `${saludo} Tienes ${d.citas} ${plural(d.citas, "cita", "citas")} hoy`;
  if (d.atendidas > 0 && d.pendientes > 0) t += `; ya ${plural(d.atendidas, "va", "van")} ${d.atendidas} y ${plural(d.pendientes, "falta", "faltan")} ${d.pendientes}`;
  t += ".";
  if (d.pendientes === 0) return `${t} Ya no te queda ninguna por atender. Buen trabajo.`;
  if (d.proximaHora) t += ` La próxima es a ${horaHablada(d.proximaHora)}.`;
  t += d.sinConfirmar > 0 ? ` ${d.sinConfirmar === 1 ? "Hay una sin confirmar" : `Hay ${d.sinConfirmar} sin confirmar`}. ¿Abro los mensajes?` : " Todas están confirmadas.";
  return t;
}

/* ---------- preparar el texto para que suene bien ---------- */

/** Quita lo que la voz leería mal (símbolos, comillas, emojis) y escribe en palabras lo que conviene. */
export function textoParaVoz(texto: string): string {
  return texto
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\bHC\s*(\d+)/gi, "historia clínica $1")
    .replace(/\bDra\.?(?=\s)/g, "doctora").replace(/\bDr\.?(?=\s)/g, "doctor")
    .replace(/\$\s?(\d+)[.,](\d{2})\b/g, (_m, e: string, c: string) => `${e} ${Number(e) === 1 ? "dólar" : "dólares"}${Number(c) ? ` con ${Number(c)} centavos` : ""}`)
    .replace(/\$\s?(\d+)\b/g, (_m, e: string) => `${e} ${Number(e) === 1 ? "dólar" : "dólares"}`)
    .replace(/(\d+)\s?%/g, "$1 por ciento")
    .replace(/&/g, " y ")
    .replace(/[«»"“”*_#`~^<>|\\]/g, "")
    .replace(/\p{Extended_Pictographic}|️|‍/gu, "")
    .replace(/…/g, ".").replace(/\.{2,}/g, ".")
    .replace(/\s*\n+\s*/g, ". ").replace(/\s+/g, " ").trim();
}

/** Parte el texto en frases cortas: los navegadores cortan las voces cuando el texto es largo. */
export function trozosParaVoz(texto: string, max = 180): string[] {
  const frases = texto.split(/(?<=[.!?:;])\s+/).map((s) => s.trim()).filter(Boolean);
  const trozos: string[] = [];
  let actual = "";
  const partir = (s: string): string[] => {
    if (s.length <= max) return [s];
    const m = s.slice(0, max).lastIndexOf(" ");
    const corte = m > 40 ? m : max;
    return [s.slice(0, corte).trim(), ...partir(s.slice(corte).trim())];
  };
  for (const f of frases.flatMap(partir)) {
    if (actual && actual.length + f.length + 1 > max) { trozos.push(actual); actual = f; } else actual = actual ? `${actual} ${f}` : f;
  }
  if (actual) trozos.push(actual);
  return trozos;
}

/* ---------- elegir la mejor voz del equipo ---------- */

export interface VozInfo { name: string; lang: string; localService?: boolean }
const FEMENINAS = ["sabina", "helena", "paola", "dalia", "elvira", "salome", "camila", "paulina", "monica", "mónica", "lucia", "lucía", "laura", "elena", "marisol", "jimena", "maria", "maría", "lupe", "valentina", "catalina", "ximena", "renata", "fernanda", "carmen", "esperanza", "sofia", "sofía", "abril", "ximena", "luciana", "marcela", "natalia", "paloma", "zira", "samantha", "female", "mujer"];
const MASCULINAS = ["jorge", "raul", "raúl", "pablo", "alvaro", "álvaro", "arnau", "alonso", "gonzalo", "lorenzo", "juan", "diego", "carlos", "miguel", "andres", "andrés", "pedro", "tomas", "tomás", "santiago", "jaime", "javier", "david", "mark", "male", "hombre"];
const ACENTOS_ES: [RegExp, number][] = [[/^es[-_]ec/i, 100], [/^es[-_]419/i, 92], [/^es[-_]mx/i, 88], [/^es[-_]co/i, 84], [/^es[-_]us/i, 80], [/^es[-_](pe|cl|ar|ve|uy|bo|py|gt|cr|pa|do|hn|ni|sv|cu|pr)/i, 72], [/^es[-_]es/i, 50], [/^es/i, 40]];

/** Qué tan buena es una voz para Donna: español latino primero, voces neuronales («natural», «online») y de mujer, sin las robóticas. */
export function puntajeVoz(v: VozInfo): number {
  const acento = ACENTOS_ES.find(([re]) => re.test(v.lang))?.[1];
  if (acento === undefined) return -1000;
  const nombre = v.name.toLocaleLowerCase("es");
  let p = acento;
  if (/natural|neural|online|premium|enhanced|wavenet|chirp|studio/.test(nombre)) p += 30;
  if (/espeak|compact/.test(nombre)) p -= 30;
  if (FEMENINAS.some((f) => nombre.includes(f))) p += 15;
  if (MASCULINAS.some((m) => nombre.includes(m))) p -= 40;
  return p;
}
/** Las voces en español, la mejor primero. */
export const vocesEnEspanol = <T extends VozInfo>(voces: readonly T[]): T[] => voces.filter((v) => puntajeVoz(v) > -1000).sort((a, b) => puntajeVoz(b) - puntajeVoz(a) || a.name.localeCompare(b.name));
/** La voz que eligió la persona si sigue disponible; si no, la mejor del equipo (o null si no hay voces en español). */
export function elegirVoz<T extends VozInfo>(voces: readonly T[], preferida = ""): T | null {
  const lista = vocesEnEspanol(voces);
  return (preferida && lista.find((v) => v.name === preferida)) || lista[0] || null;
}
